"use strict";
/**
 * Settings-modal "Connection check" control.
 *
 * Runs the /health probe and renders the verdict on the device. This is the
 * operator's only diagnostic in the field: they work from a phone, where
 * devtools cannot be opened, and the failure they need to explain happens
 * somewhere with no laptop.
 *
 * Owner-only — governed by the `connection-check` gate in gates.ts, which is
 * listed on no tier server-side and so resolves only through the owner's `"*"`
 * wildcard. This module does not check permissions itself; a gated-away
 * control is removed from view and cannot be clicked.
 *
 * Note the trade-off of living behind login: it cannot diagnose a network that
 * blocks the login request itself. That case is covered by NetworkError's
 * on-screen message instead (api/network-error.ts).
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.setupConnectionCheck = setupConnectionCheck;
const connection_check_1 = require("../services/connection-check");
const IDS = {
    button: "connection-check-btn",
    output: "connection-check-output",
    copyButton: "connection-check-copy",
};
/** Verdict → the CSS modifier used to colour the result block. */
const VERDICT_CLASS = {
    reachable: "connection-check-ok",
    "server-error": "connection-check-warn",
    refused: "connection-check-fail",
    "dns-or-tls": "connection-check-fail",
    blackholed: "connection-check-fail",
};
const VERDICT_LABEL = {
    reachable: "Server reachable",
    "server-error": "Server reachable, but erroring",
    refused: "Connection refused",
    "dns-or-tls": "DNS or TLS failure",
    blackholed: "No response (packets dropped)",
};
/** How long a verdict stays on screen before clearing itself. */
const RESULT_VISIBLE_MS = 4000;
/** Last probe, kept so the copy button can report it without re-running. */
let lastResult = null;
/** Pending auto-hide, so a re-run restarts the countdown instead of stacking. */
let hideTimeoutId = null;
function cancelAutoHide() {
    if (hideTimeoutId !== null) {
        window.clearTimeout(hideTimeoutId);
        hideTimeoutId = null;
    }
}
function scheduleAutoHide() {
    cancelAutoHide();
    hideTimeoutId = window.setTimeout(() => {
        hideTimeoutId = null;
        document.getElementById(IDS.output)?.classList.add("hidden");
    }, RESULT_VISIBLE_MS);
}
function render(result) {
    const output = document.getElementById(IDS.output);
    if (!output)
        return;
    output.className = `connection-check-output ${VERDICT_CLASS[result.verdict]}`;
    output.classList.remove("hidden");
    const status = result.status !== undefined ? ` · HTTP ${result.status}` : "";
    // textContent per line rather than innerHTML: result.detail is a browser
    // string, and this page must never grow an injection surface.
    output.textContent = "";
    const heading = document.createElement("div");
    heading.className = "connection-check-verdict";
    heading.textContent = `${VERDICT_LABEL[result.verdict]} · ${result.durationMs}ms${status}`;
    output.appendChild(heading);
    // Explanation only when there is something to diagnose. On a healthy
    // result the green block and the label already say it, and a sentence the
    // operator reads once and never again is just noise in a modal they open
    // for other reasons.
    if (result.verdict !== "reachable") {
        const explanation = document.createElement("div");
        explanation.className = "connection-check-explanation";
        explanation.textContent = result.explanation;
        output.appendChild(explanation);
    }
    // The copy button deliberately does NOT auto-hide: the report is the reason
    // to run this at all, and losing the ability to send it after four seconds
    // would mean re-running the probe to get it back.
    const copyButton = document.getElementById(IDS.copyButton);
    copyButton?.classList.remove("hidden");
    scheduleAutoHide();
}
async function copyReport() {
    if (!lastResult)
        return;
    const report = (0, connection_check_1.formatDiagnosticReport)(lastResult);
    const copyButton = document.getElementById(IDS.copyButton);
    try {
        await navigator.clipboard.writeText(report);
        if (copyButton)
            copyButton.textContent = "Copied";
    }
    catch {
        // Clipboard access is denied in some mobile contexts. Falling back to
        // showing the text means the operator can still select and copy it by
        // hand rather than losing the diagnostic entirely.
        const output = document.getElementById(IDS.output);
        if (output) {
            // Clipboard denied, so the operator has to select this by hand.
            // Cancel the auto-hide and re-show: yanking the text away
            // mid-selection would defeat the fallback entirely.
            cancelAutoHide();
            output.classList.remove("hidden");
            const pre = document.createElement("pre");
            pre.className = "connection-check-report";
            pre.textContent = report;
            output.appendChild(pre);
        }
        if (copyButton)
            copyButton.textContent = "Copy failed — select below";
    }
}
/**
 * Wire the connection-check button. Safe to call when the markup is absent —
 * it simply does nothing.
 */
function setupConnectionCheck() {
    const button = document.getElementById(IDS.button);
    if (!button)
        return;
    button.addEventListener("click", async () => {
        const btn = button;
        if (btn.disabled)
            return;
        btn.disabled = true;
        const originalLabel = btn.textContent;
        btn.textContent = "Checking…";
        try {
            lastResult = await (0, connection_check_1.probeConnection)();
            render(lastResult);
        }
        finally {
            // Always restore: a blackholed probe takes 15s, and leaving a dead
            // button behind would force a page reload to retry.
            btn.disabled = false;
            btn.textContent = originalLabel;
        }
    });
    document
        .getElementById(IDS.copyButton)
        ?.addEventListener("click", () => void copyReport());
}
